import { Pencil01Icon } from '@langchain/untitled-ui-icons';

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { Popover, PopoverContent, PopoverTrigger } from '@/components/Popover';
import { Button, IconButton } from '@/design-system';
import { useUpdateFilterViewMutation } from '@/hooks/useSwr';
import { FilterViewSchema } from '@/types/schema';

export interface SaveFilterFormData {
  name: string;
  description?: string;
}

export interface SaveFilterFormProps {
  onCancel: () => void;
  onSubmit: (data: SaveFilterFormData) => void;
  submitting: boolean;
  isEdit?: boolean;
  saveAs?: boolean;
  defaultName?: string;
  defaultDescription?: string;
  error?: string;
}

export function SaveFilterForm({
  onCancel,
  onSubmit,
  submitting,
  saveAs,
  defaultName,
  defaultDescription,
  error,
}: SaveFilterFormProps) {
  'use no memo';
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SaveFilterFormData>({
    defaultValues: {
      name: defaultName,
      description: defaultDescription,
    },
  });

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-3">
      <h3 className="text-sm font-semibold">
        {defaultName
          ? 'Update filter'
          : `Save ${saveAs ? 'as new filter' : 'filter'}`}
      </h3>

      <div>
        <label className="text-sm">Name</label>
        <input
          {...register('name', { required: 'Name is required' })}
          className="w-full rounded-md border border-secondary bg-background p-2 text-sm"
          autoComplete="off"
        />
        {(error || errors.name) && (
          <span className="text-xs text-error">
            {error ?? errors.name?.message}
          </span>
        )}
      </div>

      <div>
        <label className="text-sm">Description</label>
        <input
          {...register('description')}
          className="w-full rounded-md border border-secondary bg-background p-2 text-sm"
        />
      </div>

      <div className="flex justify-end gap-2">
        <Button color="secondary" variant="outlined" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          type="submit"
          color="primary"
          disabled={submitting}
          loading={submitting}
        >
          Done
        </Button>
      </div>
    </form>
  );
}

export function EditFilterPopover(props: {
  filterView: FilterViewSchema;
  popoverPlacement: {
    side: 'top' | 'bottom' | 'left' | 'right';
    align: 'start' | 'center' | 'end';
    sideOffset: number;
  };
}) {
  const {
    side = 'right',
    align = 'start',
    sideOffset = 75,
  } = props.popoverPlacement;
  const { sessionId } = useParams();
  const [isOpen, setIsOpen] = useState(false);
  const updateFilter = useUpdateFilterViewMutation(
    sessionId,
    props.filterView.id
  );

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <IconButton
          icon={Pencil01Icon}
          label="Edit filter"
          color="secondary"
          variant="plain"
        />
      </PopoverTrigger>
      <PopoverContent side={side} align={align} sideOffset={sideOffset}>
        <SaveFilterForm
          submitting={updateFilter.isMutating}
          isEdit={true}
          onCancel={() => setIsOpen(false)}
          onSubmit={async ({ name, description }) => {
            await updateFilter.trigger({
              json: {
                display_name: name,
                description,
              },
            });
            setIsOpen(false);
          }}
          defaultName={props.filterView.display_name}
          defaultDescription={props.filterView.description}
          error={updateFilter.error?.message}
        />
      </PopoverContent>
    </Popover>
  );
}
